import { decodeToken } from 'react-jwt'

const decodeEntry = (raw: string | null): any => {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    const decoded = decodeToken(parsed?.body?.access_token) as any
    return decoded ? { parsed, decoded } : null
  } catch {
    return null
  }
}

const hasAudience = (decoded: any, audience?: string) => {
  if (!audience) return true
  const aud = decoded?.aud
  return Array.isArray(aud) ? aud.includes(audience) : aud === audience
}

// Expiração da entrada do cache Auth0 (segundos); usa o `exp` do token se faltar
const getExpiration = ({ parsed, decoded }: any): number =>
  Number(parsed?.expiresAt ?? decoded?.exp ?? 0)

// Claims decodificadas do access token Auth0 em cache (null se não houver)
const getTokenClaims = (): any => {
  try {
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE
    const prefix = `@@auth0spajs@@::${clientId}::${audience}::`
    const key = `${prefix}openid profile email offline_access`

    // Preferência: a chave exata usada no restante do app
    let entry = decodeEntry(localStorage.getItem(key))

    // Fallback (escopo do cache mudou): entre as entradas do mesmo client/audience,
    // usa a que tem o audience correto e a expiração mais recente
    if (!entry) {
      entry =
        Object.keys(localStorage)
          .filter(k => k.startsWith(prefix))
          .map(k => decodeEntry(localStorage.getItem(k)))
          .filter(e => e && hasAudience(e.decoded, audience))
          .sort((a, b) => getExpiration(b) - getExpiration(a))[0] ?? null
    }

    return entry?.decoded ?? null
  } catch {
    return null
  }
}

/**
 * Lê a claim `usuarioCompras` do access token Auth0.
 * Retorna true quando o usuário está cadastrado como comprador no Cronograma de Compras.
 *
 * Uso apenas para UI (mostrar/ocultar ações): o token não é validado aqui e a
 * autorização real é feita pela API.
 */
export const getIsUsuarioCompras = (): boolean => {
  const claims = getTokenClaims()
  const val = claims?.usuarioCompras ?? claims?.UsuarioCompras
  return val === true || String(val).toLowerCase() === 'true'
}

// Perfil do usuário (claim `role`), ex.: Administrador, AnalistaPlanejamento
export const getUserRole = (): string | null => {
  const claims = getTokenClaims()
  return claims?.role ?? null
}

const PERFIS_VISUALIZA_TODOS_COMPRADORES = [
  'Administrador',
  'AnalistaPlanejamento'
]

/**
 * Perfis que consultam qualquer comprador (filtro Compradores visível).
 * Prevalece sobre a claim `usuarioCompras`. Só UI: a API aplica a mesma regra
 * e ignora o filtro de quem não pode usá-lo.
 */
export const getVisualizaTodosCompradores = (): boolean =>
  PERFIS_VISUALIZA_TODOS_COMPRADORES.includes(getUserRole() ?? '')
